import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Typography,
  Row,
  Col,
  Tag,
  Checkbox,
  message
} from 'antd';
import { 
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminExamResultApi } from '@/services/api';

const { Title } = Typography;
const { Option } = Select;

interface ExamResultData {
  id: number;
  examName: string;
  academyCode: string;
  studentName: string;
  completionDate: string;
  gradingStatus: 'pending' | 'completed' | 'in_progress';
  isWarningSubmission?: boolean;
  warningSubmitted?: boolean;
  disqualifiedByWarning?: boolean;
  excludedByWarning?: boolean;
}

const AdminExamResultList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [examResults, setExamResults] = useState<ExamResultData[]>([]);
  const [searchExamName, setSearchExamName] = useState<string>('');
  const [searchAcademyCode, setSearchAcademyCode] = useState<string>('');
  const [searchStudentName, setSearchStudentName] = useState<string>('');
  const [searchGradingStatus, setSearchGradingStatus] = useState<string>('');
  const [showOnlyPendingGrading, setShowOnlyPendingGrading] = useState<boolean>(false);
  const [excludeWarningSubmissions, setExcludeWarningSubmissions] = useState<boolean>(true);

  const gradingStatusOptions = [
    { value: 'pending', label: '대기' },
    { value: 'in_progress', label: '진행중' },
    { value: 'completed', label: '완료' },
  ];

  // 데이터 조회
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await adminExamResultApi.list({
        examName: searchExamName || undefined,
        academyCode: searchAcademyCode || undefined,
        studentName: searchStudentName || undefined,
        gradingStatus: searchGradingStatus || undefined,
      });
      
      if (response.success && response.data) {
        const items = (response.data.items || []) as ExamResultData[];
        // [QA Fix] 채점필요 필터 및 제출완료일 최신순 정렬
        const normalizedItems = items.map((item: ExamResultData) => ({
          ...item,
          gradingStatus: (item.gradingStatus || 'pending').toLowerCase() as ExamResultData['gradingStatus'],
          isWarningSubmission: Boolean(
            item.isWarningSubmission ??
            item.warningSubmitted ??
            item.disqualifiedByWarning ??
            item.excludedByWarning ??
            false
          ),
        }));
        const sortedItems = [...normalizedItems].sort((a, b) => {
          const dateA = dayjs(a.completionDate).valueOf() || 0;
          const dateB = dayjs(b.completionDate).valueOf() || 0;
          return dateB - dateA;
        });
        setExamResults(sortedItems);
      } else {
        message.error(response.message || '데이터를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('시험 결과 조회 실패:', error);
      message.error('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    fetchData();
  };

  const handleViewExamResult = (id: number) => {
    navigate(`/admin/exam-result/detail/${id}`);
  };

  const filteredExamResults = examResults.filter(result => {
    const pendingGradingMatch = !showOnlyPendingGrading || result.gradingStatus === 'pending';
    const warningMatch = !excludeWarningSubmissions || !result.isWarningSubmission;
    return pendingGradingMatch && warningMatch;
  });

  const getGradingStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'pending':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getGradingStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return '완료';
      case 'in_progress':
        return '진행중';
      case 'pending':
        return '대기';
      default:
        return '알 수 없음';
    }
  };

  const columns = [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) => (filteredExamResults.length > 0 ? filteredExamResults.length - index : 0),
      width: 80,
      align: 'center' as const
    },
    {
      title: '시험명',
      dataIndex: 'examName',
      key: 'examName',
      width: 200,
      render: (_: string, record: ExamResultData) => (
        <Space size={4}>
          <span>{record.examName}</span>
          {record.isWarningSubmission && (
            <Tag color="red">경고 제외</Tag>
          )}
        </Space>
      )
    },
    {
      title: '학원코드',
      dataIndex: 'academyCode',
      key: 'academyCode',
      width: 120,
      align: 'center' as const
    },
    {
      title: '학생명',
      dataIndex: 'studentName',
      key: 'studentName',
      width: 120
    },
    {
      title: '응시완료일',
      dataIndex: 'completionDate',
      key: 'completionDate',
      width: 120,
      align: 'center' as const
    },
    {
      title: '채점상태',
      dataIndex: 'gradingStatus',
      key: 'gradingStatus',
      width: 120,
      align: 'center' as const,
      render: (status: string) => (
        <Tag color={getGradingStatusColor(status)}>
          {getGradingStatusText(status)}
        </Tag>
      )
    }
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>시험 성적표 관리</Title>
        </div>

        {/* 검색 영역 */}
        <Row gutter={16} align="bottom">
          <Col span={5}>
            <label className="block mb-2 text-sm font-medium">시험명</label>
            <Input
              placeholder="시험명을 입력하세요"
              value={searchExamName}
              onChange={(e) => setSearchExamName(e.target.value)}
              prefix={<SearchOutlined />}
              size="large"
            />
          </Col>
          <Col span={4}>
            <label className="block mb-2 text-sm font-medium">학원코드</label>
            <Input
              placeholder="학원코드를 입력하세요"
              value={searchAcademyCode}
              onChange={(e) => setSearchAcademyCode(e.target.value)}
              size="large"
            />
          </Col>
          <Col span={4}>
            <label className="block mb-2 text-sm font-medium">학생명</label>
            <Input
              placeholder="학생명을 입력하세요"
              value={searchStudentName}
              onChange={(e) => setSearchStudentName(e.target.value)}
              size="large"
            />
          </Col>
          <Col span={4}>
            <label className="block mb-2 text-sm font-medium">채점상태</label>
            <Select
              placeholder="채점상태 선택"
              value={searchGradingStatus || undefined}
              onChange={(value) => setSearchGradingStatus(value || '')}
              size="large"
              style={{ width: '100%' }}
              allowClear
            >
              {gradingStatusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              size="large"
              style={{ width: '100%' }}
            >
              검색
            </Button>
          </Col>
        </Row>
        <Row className='py-4'>
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <Checkbox
                checked={showOnlyPendingGrading}
                onChange={(e) => setShowOnlyPendingGrading(e.target.checked)}
              >
                채점 필요한 시험만 보기
              </Checkbox>
              <Checkbox
                checked={excludeWarningSubmissions}
                onChange={(e) => setExcludeWarningSubmissions(e.target.checked)}
              >
                경고 누적 제출 시험 제외
              </Checkbox>
            </div>
          </Col>
        </Row>

        {/* 테이블 */}
        <Table
          columns={columns}
          dataSource={filteredExamResults}
          rowKey="id"
          loading={loading}
          onRow={(record) => ({
            onClick: () => handleViewExamResult(record.id),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            total: filteredExamResults.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => {
              if (!total) return `0-0 / 총 0개`;
              const start = Math.max(total - range[1] + 1, 1);
              const end = Math.max(total - range[0] + 1, 1);
              return `${start}-${end} / 총 ${total}개`;
            }
          }}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminExamResultList; 